<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import { BellIcon } from "lucide-vue-next";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getConsumer } from "@/lib/cable";
import type { NotificationItem, NotificationsPayload, RealtimeNotification } from "@/types";

interface SharedProps {
  notifications?: NotificationsPayload;
}

const page = usePage<SharedProps>();
const initial = page.props.notifications;

const items = ref<NotificationItem[]>(
  (initial?.items ?? []).filter((item) => !item.read_at),
);
const unreadCount = ref(initial?.unread_count ?? 0);

watch(
  () => page.props.notifications,
  (next) => {
    if (!next) return;
    items.value = (next.items ?? []).filter((item) => !item.read_at);
    unreadCount.value = next.unread_count ?? 0;
  },
  { deep: true },
);

// Subscribe to real-time notifications using Action Cable (deferred to onMounted
// so the WebSocket consumer is only created on authenticated pages).
let subscription: { unsubscribe: () => void } | undefined;

onMounted(() => {
  subscription = getConsumer().subscriptions.create(
    { channel: "WebNotificationsChannel" },
    {
      initialized() {
        console.debug("[NotificationsDropdown] Subscription initialized");
      },
      connected() {
        console.debug("[NotificationsDropdown] Connected");
      },
      disconnected() {
        console.debug("[NotificationsDropdown] Disconnected");
      },
      rejected() {
        console.warn("[NotificationsDropdown] Rejected");
      },
      received(data: RealtimeNotification) {
        console.debug("[NotificationsDropdown] Received:", data);
        if (data.type !== "noticed.notification") return;
        const notification = data.notification;

        const existingIndex = items.value.findIndex((item) => item.id === notification.id);
        if (existingIndex === -1) {
          items.value = [notification, ...items.value].slice(0, 10);
        }

        if (!notification.read_at) {
          unreadCount.value = unreadCount.value + 1;
        }
      },
    },
  );
});

onBeforeUnmount(() => {
  subscription?.unsubscribe();
});

const csrfToken = computed(() => {
  if (typeof document === "undefined") return "";
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
});

const markAsRead = async (notification: NotificationItem) => {
  if (notification.read_at) return;

  try {
    const response = await fetch(notification.read_path, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken.value,
      },
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    items.value = items.value.filter((item) => item.id !== notification.id);
    unreadCount.value = Math.max(0, unreadCount.value - 1);
  } catch (error) {
    console.error("Failed to mark notification as read", error);
  }
};

const markAllAsRead = async () => {
  const unreadItems = items.value.filter((item) => !item.read_at);
  for (const notification of unreadItems) {
    await markAsRead(notification);
  }
};

const handleSelect = (notification: NotificationItem) => {
  void markAsRead(notification);
  if (notification.url) {
    router.visit(notification.url);
  }
};

const formatTimestamp = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" size="icon-lg" class="group relative">
        <BellIcon :class="['size-5', unreadCount > 0 && 'text-destructive']" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" class="w-80 p-0">
      <div class="flex items-center justify-between px-3 py-2">
        <DropdownMenuLabel class="p-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Notifications
        </DropdownMenuLabel>
        <Button
          type="button"
          size="xs"
          variant="ghost"
          :disabled="items.length === 0"
          @click="markAllAsRead"
        >
          Mark all read
        </Button>
      </div>
      <DropdownMenuSeparator />
      <div class="max-h-80 overflow-y-auto">
        <div
          v-if="items.length === 0"
          class="px-4 py-6 text-center text-sm text-muted-foreground"
        >
          You're all caught up.
        </div>
        <DropdownMenuItem
          v-for="notification in items"
          :key="notification.id"
          class="hover:bg-accent flex cursor-pointer items-start gap-3 px-4 py-3 text-left text-sm"
          :class="!notification.read_at && 'bg-accent/40'"
          @select.prevent="handleSelect(notification)"
        >
          <span
            class="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
            :class="notification.read_at ? 'bg-transparent' : 'bg-emerald-500'"
          />
          <div class="flex-1 space-y-1">
            <div class="flex items-center justify-between gap-2">
              <p class="font-medium text-foreground">{{ notification.title }}</p>
              <span class="text-[10px] text-muted-foreground">
                {{ formatTimestamp(notification.created_at) }}
              </span>
            </div>
            <p
              v-if="notification.body"
              class="text-xs text-muted-foreground line-clamp-2"
            >
              {{ notification.body }}
            </p>
          </div>
        </DropdownMenuItem>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
